/**
 * Thin wrapper around the User Timing API (performance.mark / performance.measure).
 *
 * Adds named marks and measures for the app's key user experiences so they show
 * up in Chrome DevTools (Performance panel → "Timings" track), Lighthouse's
 * "User Timing marks and measures" audit, and any RUM/analytics tool that reads
 * the performance timeline.
 *
 * Every function is a safe no-op when the Performance API is unavailable
 * (SSR / very old browsers), so call sites never need to guard.
 */

const perf: Performance | undefined =
  typeof performance !== "undefined" && typeof performance.mark === "function"
    ? performance
    : undefined;

/** Prefix so all our entries are easy to spot/filter in the timeline. */
const NS = "mrpanda";

/** Record a single point-in-time mark, e.g. `mark("home:products-loaded")`. */
export function mark(name: string): void {
  if (!perf) return;
  try {
    perf.mark(`${NS}:${name}`);
  } catch {
    /* marks are best-effort instrumentation; never throw */
  }
}

/**
 * Record a measure between two marks (or from a start mark to now). Returns the
 * measured duration in ms when available.
 */
export function measure(name: string, startMark: string, endMark?: string): number | undefined {
  if (!perf) return undefined;
  try {
    const entry = perf.measure(
      `${NS}:${name}`,
      `${NS}:${startMark}`,
      endMark ? `${NS}:${endMark}` : undefined,
    );
    return entry?.duration;
  } catch {
    return undefined;
  }
}

/**
 * Measure from the page's time origin (navigation start) to now. Useful for
 * "time to X" milestones such as "time to first products".
 */
export function measureFromStart(name: string): number | undefined {
  if (!perf) return undefined;
  try {
    const entry = perf.measure(`${NS}:${name}`, { start: 0 });
    return entry?.duration;
  } catch {
    return undefined;
  }
}

/**
 * Time a key experience: marks a start, and returns a function that, when
 * called, marks the end and emits a measure. Ideal for instrumenting flows
 * whose start and end happen in different places.
 *
 * ```ts
 * const done = startTiming("add-to-cart");
 * // ...later
 * done();
 * ```
 */
export function startTiming(name: string): () => number | undefined {
  const startName = `${name}:start`;
  mark(startName);
  let ended = false;
  return () => {
    if (ended) return undefined;
    ended = true;
    const endName = `${name}:end`;
    mark(endName);
    return measure(name, startName, endName);
  };
}

/**
 * Convenience for timing a (possibly async) function and emitting a measure for
 * its duration. Re-throws so callers keep their own error handling.
 */
export async function timeAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const done = startTiming(name);
  try {
    return await fn();
  } finally {
    done();
  }
}